import { Box, Grid, Theme, Tooltip, Typography } from '@material-ui/core';
import {
  CheckBox,
  CheckBoxOutlineBlank,
  Delete,
  Info,
} from '@material-ui/icons';
import { Alert, AlertTitle } from '@material-ui/lab';
import { CustomQuote } from '@roc/feature-types';
import { isNil } from '@roc/feature-utils';
import { Button, ConfirmDialog, IconButton, StandardDialog } from '@roc/ui';
import { formatCurrency, formatPercentage } from '@roc/ui/utils';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import React, { useState } from 'react';

import { TableHead } from '@material-ui/core';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import Toolbar from '@material-ui/core/Toolbar';
import { createStyles, makeStyles, withStyles } from '@material-ui/core/styles';
import { Add, Check, Edit } from '@material-ui/icons';
import {
  DEFAULT_FEE_CORELOGIX,
  DEFAULT_FEE_RETAIL_SERVICING_SETUP,
} from '@roc/feature-loans';
import {
  CurrencyField,
  PercentageField,
  IconButton as RocIconButton,
  SelectField,
} from '@roc/ui';
import PricerSummaryStore from '../../stores/singlePropertyPricer/pricerSummaryStore';
import { set } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';
import { CUSTOM_QUOTE_FEE_CATEGORIES } from '../../stores/singlePropertyPricer/pricerSummaryHelper';
import { PercentagePicker, PricerRateSlider } from './pricerRateSlider';

interface FeesDialogProps {
  open: boolean;
  onClose: () => void;
  cRate: CustomQuote;
  quoteIndex: number;
  classes: any;
  isBroker: boolean;
  pricerSummaryStore: any;
  theme: Theme;
  rateAvailable: boolean;
  setRateTypeOptionsBasedOnProgramType: () => any[];
  handleChangeCommittedRateSlider: (
    cRate: CustomQuote,
    quoteIndex: number,
    value: number
  ) => void;
  isPricingTab: boolean;
}

interface PricerSummaryV2QuoteProps {
  cRate: CustomQuote;
  quoteIndex: number;
  isExpanded: boolean;
  handleChange: (
    panel: string
  ) => (event: React.ChangeEvent<{}>, newExpanded: boolean) => void;
  panelName: string;
  isBackOfficeProcessor: boolean;
  allowLoanPricing: boolean;
  classes: any;
  isBroker: boolean;
  pricerSummaryStore: any;
  theme: Theme;
  rateAvailable: boolean;
  goodToSubmitQuote: boolean;
  setShowLoanRatesDialog: (value: boolean) => void;
  customQuotes: CustomQuote[];
  setIsConfirmSubmitOpen: (value: boolean) => void;
  setQuoteIndexDuringConfirm: (value: number) => void;
  handleSubmitQuote: (quoteIndex: number) => void;
  isConfirmSubmitOpen: boolean;
  propertyType: any;
  setRateTypeOptionsBasedOnProgramType: () => any[];
  handleChangeCommittedRateSlider: (
    cRate: CustomQuote,
    quoteIndex: number,
    value: number
  ) => void;
  isPricingTab: boolean;
  isPreloadedQuote: boolean;
  handleConfirmSubmit: () => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  table: {
    '& .MuiInputBase-root *': {
      fontSize: 14,
    },
    '& .MuiSelect-root *': {
      fontSize: 14,
    },
  },
  customFieldInputContainer: {
    minWidth: 180,
    '& .currency-field-container': {
      display: 'inline-block',
      '& fieldset': {
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
      },
      '& input': {
        padding: '8px 8px 8px 0',
        textAlign: 'left',
      },
    },
    '& .percentage-field-container': {
      display: 'inline-block',
      '& fieldset': {
        borderLeftWidth: 0,
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
      },
      '& input': {
        padding: '8px 0 8px 8px',
        textAlign: 'right',
      },
    },
    '& .check-button-container': {
      display: 'inline-block',
      padding: '8px 0 8px 8px',
    },
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: 16,
  },
  parRateText: {
    fontSize: 15,
    color: '#666',
    marginBottom: 12,
  },
  controlsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 32,
  },
  pointsFeesTitle: {
    fontWeight: 600,
    marginBottom: 12,
  },
  warningAlert: {
    marginBottom: 16,
    background: '#fff3e0',
    border: '1px solid #ffb74d',
    borderRadius: 8,
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  warningIcon: {
    color: '#f57c00',
  },
  warningText: {
    fontSize: 15,
    color: '#e65100',
  },
  card: {
    flex: 1,
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: 12,
    padding: 20,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  cardTitle: {
    fontWeight: 700,
    fontSize: 14,
    color: '#222',
  },
  cardAmount: {
    fontWeight: 700,
    fontSize: 18,
    color: theme.palette.primary.dark,
  },
  infoBox: {
    background: '#e3f2fd',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
  },
  infoBoxWithMinHeight: {
    background: '#e3f2fd',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    minHeight: 88,
  },
  infoIcon: {
    color: '#1976d2',
    marginTop: 2,
    flexShrink: 0,
  },
  infoText: {
    fontSize: 14,
    color: '#1565c0',
    lineHeight: 1.5,
  },
  addFeeButton: {
    width: '100%',
    marginBottom: 16,
    fontWeight: 600,
    background: '#009688',
    color: '#fff',
  },
  feeRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  feeLabel: {
    fontSize: 14,
  },
  feeValue: {
    fontWeight: 700,
    fontSize: 14,
  },
  feeValueRed: {
    fontWeight: 700,
    fontSize: 14,
    color: '#d32f2f',
  },
  // Quote card styles
  quoteCard: {
    background: '#fff',
    borderRadius: 16,
    border: '1px solid #D1D5DC',
    padding: 24,
    marginBottom: 16,
  },
  quoteTitle: {
    fontWeight: 'bold',
    marginRight: 8,
  },
  deleteIcon: {
    color: '#f44336',
  },
  removeButton: {
    color: '#f44336',
    fontWeight: 500,
  },
  quoteWarningAlert: {
    marginBottom: 16,
    background: '#fff8e1',
    border: '1px solid #ffe0b2',
    color: '#b26a00',
  },
  quoteWarningIcon: {
    verticalAlign: 'middle',
    marginRight: 4,
  },
  quoteLabel: {
    fontSize: 14,
    color: '#6a7282',
  },
  quoteValue: {
    fontWeight: 'bold',
    fontSize: 14,
  },
  quoteValueBold: {
    fontWeight: 'bold',
    fontSize: 24,
  },
  quotePerMonthSuffix: {
    fontSize: 14,
    color: '#888',
  },
  quoteSmallText: {
    fontSize: 12,
    color: '#6a7282',
  },
  youMakeContainer: {
    minWidth: 220,
    marginLeft: 24,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  youMakeCard: {
    minWidth: 220,
    background: '#e8f5e9',
    borderRadius: 12,
    padding: 16,
    marginLeft: 24,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  youMakeLabel: {
    fontSize: 14,
    color: '#388e3c',
    fontWeight: 500,
  },
  youMakeAmount: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#388e3c',
    margin: '8px 0',
  },
  youMakePercent: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#388e3c',
  },
  allocateFeesButton: {
    marginTop: 16,
    marginBottom: 8,
    width: '100%',
  },
  submitButton: {
    width: '100%',
  },
  // RateControl styles
  rateControlContainer: {
    flex: 1,
    gap: 4,
  },
  rateControlLabel: {
    fontSize: 14,
    fontWeight: 500,
    marginBottom: 8,
  },
  rateControlButtonsContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
  },
  rateControlButton: {
    minWidth: 40,
    height: 56,
    fontWeight: 700,
  },
  // Toolbar styles
  toolbarFeeDisplay: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  toolbarFeeAmount: {
    fontSize: 24,
    fontWeight: 'bold',
    margin: '0px 8px',
  },
  editIconButton: {
    color: 'black',
  },
  tooltipPaddingRight: {
    paddingRight: '2px',
  },
  tooltipHighlight: {
    color: '#00BCD4',
  },
  // Table styles
  tableContainer: {
    marginBottom: 16,
  },
  tableCellWidth45: {
    width: '45%',
  },
  tableCellWidth10: {
    width: '10%',
  },
  tableCellMin200: {
    width: '45%',
  },
  tableCellMin200WithPadding: {
    width: '45%',
    padding: '12px 32px 12px 24px',
  },
  tableCellMax200: {
    maxWidth: 200,
    width: '45%',
  },
  tableCellTotalRow: {
    paddingTop: 16,
    paddingBottom: 16,
  },
  tableTotalLabel: {
    fontWeight: 'bold',
    textAlign: 'right',
    paddingBottom: 4,
  },
  tableTotalValue: {
    fontWeight: 'bold',
    textAlign: 'right',
  },
  tableTotalValueError: {
    fontWeight: 'bold',
    textAlign: 'right',
    color: 'red',
  },
  // Alert styles
  infoAlert: {
    marginBottom: 8,
  },
}));

export const PricerSummaryV2Quote = observer(
  (props: PricerSummaryV2QuoteProps) => {
    const {
      cRate,
      quoteIndex,
      isExpanded,
      handleChange,
      panelName,
      isBackOfficeProcessor,
      allowLoanPricing,
      classes,
      isBroker,
      pricerSummaryStore,
      theme,
      rateAvailable,
      goodToSubmitQuote,
      setShowLoanRatesDialog,
      customQuotes,
      setIsConfirmSubmitOpen,
      setQuoteIndexDuringConfirm,
      handleSubmitQuote,
      isConfirmSubmitOpen,
      propertyType,
      setRateTypeOptionsBasedOnProgramType,
      handleChangeCommittedRateSlider,
      isPricingTab,
      isPreloadedQuote,
      handleConfirmSubmit,
    } = props;

    const [isFeesDialogOpen, setIsFeesDialogOpen] = React.useState(false);
    const quoteClasses = useStyles();

    const remaining = cRate.pointsAndFees - cRate.totalAllocatedFees;
    const closingFees = getClosingFees(pricerSummaryStore);
    const totalPointsAndFees = (cRate.totalAllocatedFees || 0) + (cRate.totalNonRocFees || 0) + closingFees.reduce((acc, fee) => acc + (fee.value || 0), 0) + (cRate.fees?.addOnUwFee || 0);

    const youMakeAmount = isBroker
      ? cRate.youMakeForBroker
      : pricerSummaryStore.isInternalLender
      ? cRate.commissions?.commissionAmount || 0
      : cRate.youMakeForWholesale;
    const youMakePercent = isBroker
      ? cRate.youMakeForBrokerPercent
      : pricerSummaryStore.isInternalLender
      ? cRate.commissions?.commissionPercentage || 0
      : cRate.youMakeForWholesalePercent;

    const handleCompare = e => {
      e.stopPropagation();
      const newCRate = Object.assign({}, { ...cRate });
      const compareVaule = newCRate.compare;
      const newCompareValue = !compareVaule;
      newCRate.compare = newCompareValue;
      pricerSummaryStore.editCompareCount(newCRate, quoteIndex, false);
    };

    const handleRemove = event => {
      event.stopPropagation();
      pricerSummaryStore.removeCustomQuote(quoteIndex);
    };

    const handleSubmit = event => {
      event.stopPropagation();
      setTimeout(() => {
        (window as any).scrollTo(0, document.body.scrollHeight);
      }, 100);
      if (
        !isBroker &&
        !isBackOfficeProcessor &&
        allowLoanPricing &&
        !isPricingTab &&
        ((!pricerSummaryStore.isInternalLender &&
          cRate.youMakeForWholesale == 0) ||
          (pricerSummaryStore.isInternalLender &&
            cRate.commissions?.commissionAmount == 0))
      ) {
        setIsConfirmSubmitOpen(true);
        setQuoteIndexDuringConfirm(quoteIndex);
      } else {
        handleSubmitQuote(quoteIndex);
      }
    };

    return (
      <Box mt={3}>
        <Box className={quoteClasses.quoteCard}>
          <Box display="flex" alignItems="flex-start">
            {/* Left: Quote Info */}
            <Box flex={1} mr={2}>
              <Box display="flex" alignItems="center" mb={1}>
                <IconButton
                  testId="compareCheckBox"
                  disabled={!goodToSubmitQuote}
                  color="primary"
                  onClick={handleCompare}
                >
                  {cRate.compare && goodToSubmitQuote ? (
                    <CheckBox />
                  ) : (
                    <CheckBoxOutlineBlank />
                  )}
                </IconButton>
                <Typography variant="h6" className={quoteClasses.quoteTitle}>
                  Quote #{quoteIndex + 1}
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleRemove}
                  testId="removeQuote"
                  startIcon={
                    <Delete
                      fontSize="small"
                      className={quoteClasses.deleteIcon}
                    />
                  }
                  className={quoteClasses.removeButton}
                  disabled={isPreloadedQuote}
                >
                  Remove Quote
                </Button>
              </Box>
              {rateAvailable &&
                cRate.pointsAndFees !== cRate.totalAllocatedFees && (
                  <FeeAllocationWarning
                    totalAllocatedFees={cRate.totalAllocatedFees}
                    pointsAndFees={cRate.pointsAndFees}
                  />
                )}
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <Typography className={quoteClasses.quoteLabel}>
                    Loan To Value
                  </Typography>
                  <Typography className={quoteClasses.quoteValue}>
                    {formatPercentage(cRate.loanToValue)}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography className={quoteClasses.quoteLabel}>
                    PITIA DSCR
                  </Typography>
                  <Typography className={quoteClasses.quoteValue}>
                    {rateAvailable ? cRate.dscr : '-'}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography className={quoteClasses.quoteLabel}>
                    Rate Type
                  </Typography>
                  <Typography className={quoteClasses.quoteValue}>
                    {cRate.rateType}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography className={quoteClasses.quoteLabel}>
                    Pre-Payment Penalty
                  </Typography>
                  <Typography className={quoteClasses.quoteValue}>
                    {cRate.prePaymentPenalty}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography className={quoteClasses.quoteLabel}>
                    Loan Purpose
                  </Typography>
                  <Typography className={quoteClasses.quoteValue}>
                    {cRate.loanPurpose}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography className={quoteClasses.quoteLabel}>
                    Est. Mo. Payment
                  </Typography>
                  <Typography className={quoteClasses.quoteValue}>
                    {rateAvailable
                      ? formatCurrency(
                          cRate.pricerLoanTerms?.monthlyPaymentWithoutExpenses
                        )
                      : '-'}
                    <span className={quoteClasses.quotePerMonthSuffix}>
                      /mo
                    </span>
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography className={quoteClasses.quoteLabel}>
                    Borrower Rate
                  </Typography>
                  <Typography className={quoteClasses.quoteValueBold}>
                    {rateAvailable ? formatPercentage(cRate.rate) : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography className={quoteClasses.quoteLabel}>
                    Points/Fees
                  </Typography>
                  <Typography className={quoteClasses.quoteValueBold}>
                    {rateAvailable ? formatCurrency(totalPointsAndFees) : '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
            {/* Right: You Make & Actions */}
            <Box className={quoteClasses.youMakeContainer}>
              {!isBroker && (
              <Box className={quoteClasses.youMakeCard}>
                <Typography className={quoteClasses.youMakeLabel}>
                  You Make:
                </Typography>
                <Typography className={quoteClasses.youMakeAmount}>
                  {rateAvailable ? formatCurrency(youMakeAmount) : '-'}
                </Typography>
                <Typography className={quoteClasses.youMakePercent}>
                  {rateAvailable
                    ? `${formatPercentage(youMakePercent)} of Loan Amount`
                    : null}
                </Typography>
              </Box>
              )}
              <Button
                variant="outlined"
                color="primary"
                className={quoteClasses.allocateFeesButton}
                onClick={() => setIsFeesDialogOpen(true)}
                testId="editLoanTermsAndFees"
                disabled={isPreloadedQuote}
              >
                Allocate Fees / YSP
              </Button>
              <Button
                variant="contained"
                color="primary"
                className={quoteClasses.submitButton}
                disabled={isPreloadedQuote || !rateAvailable || !goodToSubmitQuote}
                onClick={handleSubmit}
                testId="continueSubmission"
              >
                Submit
              </Button>
              <ConfirmDialog
                open={isConfirmSubmitOpen}
                body={`Your expected earnings for this loan are $0. Are you sure you want to proceed?`}
                handleCancel={() => setIsConfirmSubmitOpen(false)}
                handleClose={() => setIsConfirmSubmitOpen(false)}
                handleConfirm={handleConfirmSubmit}
                confirmButtonText={'Yes'}
                cancelButtonText={'No'}
              />
            </Box>
          </Box>
        </Box>
        <FeesDialog
          open={isFeesDialogOpen}
          onClose={() => setIsFeesDialogOpen(false)}
          cRate={cRate}
          quoteIndex={quoteIndex}
          classes={classes}
          isBroker={isBroker}
          pricerSummaryStore={pricerSummaryStore}
          theme={theme}
          rateAvailable={rateAvailable}
          setRateTypeOptionsBasedOnProgramType={
            setRateTypeOptionsBasedOnProgramType
          }
          handleChangeCommittedRateSlider={handleChangeCommittedRateSlider}
          isPricingTab={isPricingTab}
        />
      </Box>
    );
  }
);

const FeesDialog = observer((props: FeesDialogProps) => {
  const {
    open,
    onClose,
    cRate,
    quoteIndex,
    isBroker,
    pricerSummaryStore,
    theme,
    rateAvailable,
    setRateTypeOptionsBasedOnProgramType,
    handleChangeCommittedRateSlider,
    isPricingTab,
  } = props;
  const store = pricerSummaryStore;
  const classes2 = useStyles();
  const remaining = cRate.pointsAndFees - cRate.totalAllocatedFees;

  const classes = { ...props.classes, ...classes2 };

  const dialogContent = (
    <Grid container spacing={4}>
      <Grid container>
        <Grid
          item
          xs={3}
          style={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box
            textAlign={'center'}
            mb={2}
            display={'flex'}
            justifyContent={'center'}
            alignItems={'center'}
          >
            <Box
              textAlign={'center'}
              ml={2}
              display={'flex'}
              flexDirection={'column'}
            >
              <PercentagePicker
                fieldName={'interestRate'}
                label="Rate"
                disabled={false}
                value={cRate.rate}
                min={cRate.minRate}
                max={cRate.maxRate}
                onChange={value => {
                  let newRate = Number(value);
                  cRate.rate = newRate;
                  pricerSummaryStore.editCustomQuote(
                    cRate,
                    quoteIndex,
                    false,
                    false
                  );
                }}
                onBlur={() => {
                  let newRate = cRate.rate;
                  pricerSummaryStore.adjustRatesAndLoanTermsForCustomQuote(
                    cRate,
                    quoteIndex,
                    newRate
                  );
                }}
              />
            </Box>
          </Box>
        </Grid>
        {rateAvailable && (
          <Grid item xs={9}>
            <PricerRateSlider
              rate={cRate.rate}
              minRate={cRate.minRate}
              maxRate={cRate.maxRate}
              yieldSpread={cRate.yieldSpread}
              onChange={value =>
                handleChangeCommittedRateSlider(cRate, quoteIndex, value)
              }
            />
          </Grid>
        )}
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h6" className={classes.pointsFeesTitle}>
          Points / Fees
        </Typography>
      </Grid>
      {rateAvailable && cRate.pointsAndFees !== cRate.totalAllocatedFees && (
        <Grid item xs={12}>
          <FeeAllocationWarning
            totalAllocatedFees={cRate.totalAllocatedFees}
            pointsAndFees={cRate.pointsAndFees}
          />
        </Grid>
      )}
      <Grid item xs={4}>
        {/* Buydown Points/Fees Card */}
        <CapitalProviderFeesTable
          handleAdd={section =>
            pricerSummaryStore.addCustomQuoteFeeChange(quoteIndex, section)
          }
          handleDelete={i =>
            pricerSummaryStore.deleteCustomQuoteFeeChange(quoteIndex, i)
          }
          handleChange={(i, k, v) =>
            pricerSummaryStore.handleCustomQuoteFeeChange(quoteIndex, i, k, v)
          }
          handleBlur={(i, k) =>
            pricerSummaryStore.handleCustomQuoteFeeChangeOnBlur(
              quoteIndex,
              i,
              k
            )
          }
          customQuote={cRate}
          pricerSummaryStore={pricerSummaryStore}
          quoteIndex={quoteIndex}
          isInternal={pricerSummaryStore.isInternalLender}
          isPricingTab={isPricingTab}
        />
      </Grid>
      {( !isPricingTab && (
      <Grid item xs={4}>
        {/* Non-Buydown Fees Card */}
        <LenderFeesTable
          handleAdd={section =>
            pricerSummaryStore.addCustomQuoteFeeChange(quoteIndex, section)
          }
          handleDelete={i =>
            pricerSummaryStore.deleteCustomQuoteFeeChange(quoteIndex, i)
          }
          handleChange={(i, k, v) =>
            pricerSummaryStore.handleCustomQuoteFeeChange(quoteIndex, i, k, v)
          }
          handleBlur={(i, k) =>
            pricerSummaryStore.handleCustomQuoteFeeChangeOnBlur(
              quoteIndex,
              i,
              k
            )
          }
          customQuote={cRate}
          pricerSummaryStore={pricerSummaryStore}
          quoteIndex={quoteIndex}
          isInternal={pricerSummaryStore.isInternalLender}
          isPricingTab={isPricingTab}
        />
      </Grid>
      ))}
      <Grid item xs={4}>
        <Grid item xs={12}>
          <OtherFeesCard customQuote={cRate} />
        </Grid>
        <Grid item xs={12}>
          <ClosingFeesCard store={store} />
        </Grid>
      </Grid>
    </Grid>
  );

  const dialogActions = (
    <Button onClick={onClose} variant="outlined" testId="cancel">
      CLOSE
    </Button>
  );

  return (
    <StandardDialog
      open={open}
      onClose={onClose}
      handleClose={onClose}
      title="Allocate Fees / Yield Spread Premium"
      maxWidth="lg"
      fullWidth
      dialogContent={dialogContent}
      dialogActions={dialogActions}
    />
  );
});

interface RateControlProps {
  testId: string;
  label: string;
  value: string | number;
  onChange: (value: number) => void;
}

const RateControl = observer((props: RateControlProps) => {
  const { label, value, onChange } = props;
  const rateClasses = useStyles();

  const increment = 0.125;
  const _value = Math.max(0, Math.min(100, Number(value)));

  return (
    <Box flex={1} className={rateClasses.rateControlContainer}>
      <Typography className={rateClasses.rateControlLabel}>{label}</Typography>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        className={rateClasses.rateControlButtonsContainer}
      >
        <Button
          testId="decrease"
          variant="outlined"
          className={rateClasses.rateControlButton}
          onClick={() => onChange(Math.max(0, _value - increment))}
        >
          -
        </Button>
        <PercentageField
          testId={props.testId}
          allowNegative={false}
          variant="outlined"
          decimalScale={2}
          value={value}
          fullWidth
          onChange={(_, value) => onChange(_value)}
        />
        <Button
          testId="increase"
          variant="outlined"
          className={rateClasses.rateControlButton}
          onClick={() => onChange(Math.min(100, _value + increment))}
        >
          +
        </Button>
      </Box>
    </Box>
  );
});

export function createData(
  category: string,
  name: string,
  amount: number,
  percent: number
) {
  return { category, name, amount, percent };
}

const useToolbarStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: {
      flex: '1 1 100%',
    },
    tooltipRoot: {
      width: '150px',
      justifyContent: 'space-between',
    },
    tooltipWrapper: {
      display: 'flex',
    },
    tooltipText: {
      fontSize: '0.8rem',
    },
    separateToRight: {
      marginRight: '8px',
    },
  })
);

const HtmlTooltip = withStyles(theme => ({
  tooltip: {
    backgroundColor: 'white',
    color: 'rgba(0, 0, 0, 0.87)',
    border: '1px solid #222222',
    marginBottom: '6px',
  },
}))(Tooltip);

const EnhancedTableToolbar = ({
  title,
  customQuote,
  showFees = false,
  pricerSummaryStore,
  quoteIndex,
  isInternal,
}) => {
  const generalClasses = useStyles();
  const classes = useToolbarStyles();
  const customFieldClasses = useStyles();
  const rateAvailable = !isNil(customQuote.rate);
  const [editFeesMode, setEditFeesMode] = React.useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
  const [currencyValue, setCurrencyValue] = React.useState(customQuote.pointsAndFees);
  const [percentValue, setPercentValue] = React.useState(customQuote.pointsAndFeesPercent);

  const handleConfirm = () => {
    setIsConfirmOpen(false);
    // timeout is needed because if modal closes the input looses the focus.
    setTimeout(() => {
      setEditFeesMode(true);
    }, 100);
  };

  return (
    <Box display="flex">
      <Box flex={1}>
        <Typography className={generalClasses.cardTitle}>{title}</Typography>
      </Box>
      <Box>
        {showFees && rateAvailable && customQuote ? (
          editFeesMode ? (
            <Box>
              <HtmlTooltip
                open={true}
                title={
                  <Box
                    className={`${classes.tooltipRoot} ${classes.tooltipWrapper}`}
                  >
                    <Box
                      className={`${classes.tooltipWrapper} ${classes.separateToRight}`}
                    >
                      <Typography
                        className={classes.tooltipText}
                        style={{ paddingRight: '2px' }}
                      >
                        Min:
                      </Typography>
                      <Typography
                        className={`${classes.tooltipText} ${customFieldClasses.tooltipHighlight}`}
                      >
                        $0
                      </Typography>
                    </Box>
                    <Box className={classes.tooltipWrapper}>
                      <Typography
                        className={classes.tooltipText}
                        style={{ paddingRight: '2px' }}
                      >
                        Max:
                      </Typography>
                      <Typography
                        className={`${classes.tooltipText} ${customFieldClasses.tooltipHighlight}`}
                      >
                        {formatCurrency(customQuote.maxPointsAndFees)}
                      </Typography>
                    </Box>
                  </Box>
                }
                arrow
                placement="top-start"
              >
                <p></p>
              </HtmlTooltip>
              <Box className={customFieldClasses.customFieldInputContainer}>
                <Box className={'currency-field-container'} style={{ maxWidth: 80}}>
                  <CurrencyField
                    testId="editFeesInput"
                    value={currencyValue}
                    variant={'outlined'}
                    onChange={(_, value) => {
                      const result = pricerSummaryStore.handleRateBuydownChange(
                        value,
                        currencyValue,
                        percentValue,
                        customQuote,
                        'amount'
                      );
                      setCurrencyValue(result.currentCurrencyValue);
                      setPercentValue(result.currentPercentValue);
                    }}
                    onBlur={() => {
                      setPercentValue(Number(
                        (currencyValue * 100) /
                        customQuote.pricerLoanTerms.amount
                      ).toFixed(2));
                    }}
                  />
                </Box>
                <Box className={'percentage-field-container'} style={{ maxWidth: 80 }}>
                  <PercentageField
                    testId="editFeesByPercentageInput"
                    value={percentValue}
                    variant={'outlined'}
                    onChange={(_, value) => {
                      const result = pricerSummaryStore.handleRateBuydownChange(
                        value,
                        currencyValue,
                        percentValue,
                        customQuote,
                        'percent'
                      );
                      setCurrencyValue(result.currentCurrencyValue);
                      setPercentValue(result.currentPercentValue);
                    }}
                    onBlur={() => {
                      setCurrencyValue(Number(
                        (
                          (customQuote.pricerLoanTerms.amount *
                            percentValue) /
                          100
                        ).toFixed(2)
                      ));
                    }}
                  />
                </Box>
                <Box className={'check-button-container'}>
                  <IconButton
                    testId="check-button"
                    color="primary"
                    aria-label="enter"
                    size="small"
                  >
                    <Check
                      fontSize="small"
                      onClick={event => {
                        customQuote.pointsAndFees = currencyValue;
                        customQuote.pointsAndFeesPercent = percentValue;
                        customQuote.buydown = customQuote.pointsAndFees;
                        pricerSummaryStore.handleRateBuydownSubmit(quoteIndex);
                        setEditFeesMode(false);
                        customQuote.yieldSpread = 0;
                        pricerSummaryStore.editCustomQuote(
                          customQuote,
                          quoteIndex,
                          true
                        );
                      }}
                    />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box className={customFieldClasses.toolbarFeeDisplay}>
              <Typography className={generalClasses.cardAmount}>
                {formatCurrency(customQuote.pointsAndFees)}
              </Typography>
              <RocIconButton size="small" testId="reduceRate">
                <Edit
                  fontSize="small"
                  className={customFieldClasses.editIconButton}
                  onClick={event => {
                    setIsConfirmOpen(true);
                  }}
                />
              </RocIconButton>
            </Box>
          )
        ) : (
          ''
        )}
      </Box>
      <ConfirmDialog
        open={isConfirmOpen}
        body={`By changing these fees, the rate will be reset to the lowest possible and the ${
          isInternal ? 'Buydown' : 'Capital Provider'
        } Points/Fees section will also get reset. Are you sure you want to continue?`}
        handleCancel={() => setIsConfirmOpen(false)}
        handleClose={() => setIsConfirmOpen(false)}
        handleConfirm={handleConfirm}
        confirmButtonText={'Continue'}
        cancelButtonText={'Cancel'}
      />
    </Box>
  );
};

const CustomFeeAndPercentField = observer(
  ({
    isEditing,
    name,
    amount,
    percent,
    disabled = false,
    onChange,
    onBlur,
  }) => {
    const classes = useStyles();
    return (
      <Box className={classes.customFieldInputContainer}>
        <Box className="currency-field-container">
          {isEditing ? (
            <CurrencyField
              disabled={disabled}
              variant={'outlined'}
              testId={name}
              value={isNil(amount) ? '' : amount}
              onChange={(_, newValue) => onChange('amount', newValue)}
              onBlur={() => onBlur('amount')}
            />
          ) : (
            <>{isNil(amount) ? '' : formatCurrency(amount)}</>
          )}
        </Box>
        <Box className="percentage-field-container">
          {isEditing ? (
            <PercentageField
              disabled={disabled}
              variant={'outlined'}
              testId={name}
              value={isNil(percent) ? '' : percent}
              onChange={(_, newValue) => onChange('percent', newValue)}
              onBlur={() => onBlur('percent')}
            />
          ) : (
            <>{isNil(percent) ? '' : formatPercentage(percent)}</>
          )}
        </Box>
      </Box>
    );
  }
);

const feeOptions = [
  { label: 'Admin Fee', value: 'Admin Fee' },
  { label: 'Underwriting Fee', value: 'Underwriting Fee' },
  { label: 'Commitment Fee', value: 'Commitment Fee' },
  { label: 'Buydown Fee', value: 'Buydown Fee' },
  { label: 'Origination Points', value: 'Origination Points' },
  { label: 'Processing Fee', value: 'Processing Fee' },
];

const CapitalProviderFeesTable = observer(
  ({
    customQuote,
    handleAdd,
    handleDelete,
    handleChange,
    handleBlur,
    pricerSummaryStore,
    quoteIndex,
    isInternal,
    isPricingTab = false,
  }) => {
    const classes = useStyles();
    const loanAmount = pricerSummaryStore.calculateLoanAmount(quoteIndex);
    const remaining =
      customQuote.pointsAndFees - customQuote.totalAllocatedFees;
    const isBroker = pricerSummaryStore.isBroker;

    const handleAddFee = (feeName, feeAmount, feePercent) => {
      const category = 'Capital Provider';
      const customIdForRow = uuidv4();
      const customQuote = pricerSummaryStore.customQuotes[quoteIndex];
      const key = pricerSummaryStore.getCustomQuoteFeeKey(feeName, category);
      pricerSummaryStore.updateFeesOnQuoteChange(quoteIndex, key, feeAmount, feePercent);
      customQuote.feesRows.push({
        category,
        name: feeName,
        amount: feeAmount,
        percent: feePercent,
        key: key,
        rowId: customIdForRow,
      });
      pricerSummaryStore.calculateTotalFeeForCustomQuote(
        quoteIndex,
        loanAmount
      );
    };

    const fees = [
      ...customQuote.feesRows
        .map((row, i) => ({
          index: i,
          label: row.name,
          value: row.amount,
          category: row.category,
        })),
    ].filter(Boolean);
    const excludedCapitalProviderFeeOptions = fees.filter(row => row.category === 'Capital Provider').map(row => row.label);

    return (
      <Box className={classes.card}>
        <EnhancedTableToolbar
          title={
            isInternal || isBroker
              ? 'Buydown Points/Fees'
              : 'Capital Provider Buydown Points/Fees'
          }
          customQuote={customQuote}
          showFees={true}
          pricerSummaryStore={pricerSummaryStore}
          quoteIndex={quoteIndex}
          isInternal={isInternal}
        />
        <CapitalProviderFeesAlert isBroker={isBroker} />
        <AddFeeButton
          pricerSummaryStore={pricerSummaryStore}
          loanAmount={loanAmount}
          onAddFee={handleAddFee}
          feeOptions={feeOptions.filter(option => !excludedCapitalProviderFeeOptions.includes(option.value))} />
        <Box>
          {fees.filter(row => row.category === 'Capital Provider').map((fee, index) => (
            <Box className={classes.feeRow} key={index}>
              <Typography className={classes.feeLabel}>{fee.label}:</Typography>
              <Typography className={classes.feeValue}>
                {formatCurrency(fee.value)}
                <IconButton
                  testId="check-button"
                  aria-label="delete"
                  size="small"
                >
                  <Delete
                    fontSize="small"
                    onClick={event => {
                      handleDelete(fee.index);
                      excludedCapitalProviderFeeOptions.splice(excludedCapitalProviderFeeOptions.indexOf(fee.label), 1);
                    }}
                  />
                </IconButton>
              </Typography>
            </Box>
          ))}
          <Box
            style={{ margin: '12px 0', borderBottom: '1px solid #e5e7eb' }}
          />
          <Box className={classes.feeRow}>
            <Typography className={classes.feeLabel}>
              Total allocated fees:
            </Typography>
            <Typography className={classes.feeValue}>
              {formatCurrency(customQuote.totalAllocatedFees)}
            </Typography>
          </Box>
          <Box className={classes.feeRow}>
            <Typography className={classes.feeLabel}>Remaining:</Typography>
            <Typography
              className={remaining > 0 ? classes.feeValueRed : classes.feeValue}
            >
              {formatCurrency(remaining)}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }
);

const LenderFeesTable = observer(
  ({
    customQuote,
    handleAdd,
    handleDelete,
    handleChange,
    handleBlur,
    pricerSummaryStore,
    quoteIndex,
    isInternal,
    isPricingTab = false,
  }) => {
    const classes = useStyles();
    const loanAmount = pricerSummaryStore.calculateLoanAmount(quoteIndex);
    const remaining =
      customQuote.pointsAndFees - customQuote.totalAllocatedFees;
    const isBroker = pricerSummaryStore.isBroker;

    const handleAddFee = (feeName, feeAmount, feePercent) => {
      const category = 'Lender';
      const customIdForRow = uuidv4();
      const customQuote = pricerSummaryStore.customQuotes[quoteIndex];
      const key = pricerSummaryStore.getCustomQuoteFeeKey(feeName, category);
      pricerSummaryStore.updateFeesOnQuoteChange(quoteIndex, key, feeAmount, feePercent);
      customQuote.feesRows.push({
        category,
        name: feeName,
        amount: feeAmount,
        percent: feePercent,
        key: key,
        rowId: customIdForRow,
      });
      pricerSummaryStore.calculateTotalFeeForCustomQuote(
        quoteIndex,
        loanAmount
      );
      if (customQuote.rate && category === CUSTOM_QUOTE_FEE_CATEGORIES.LENDER) {
        pricerSummaryStore.calculateCommissionsForCustomQuote(
          customQuote,
          quoteIndex,
          loanAmount
        );
      }
    };

    const fees = customQuote.feesRows
      .map((row, i) => ({
        index: i,
        label: row.name,
        value: row.amount,
        category: row.category,
      }));
    const excludedLenderFeeOptions = fees.filter(row => row.category === 'Lender').map(row => row.label);

    return (
      <Box className={classes.card}>
        <EnhancedTableToolbar
          title={isInternal || isBroker ? 'Non-Buydown Fees' : 'Your Fees'}
          customQuote={customQuote}
          pricerSummaryStore={pricerSummaryStore}
          quoteIndex={quoteIndex}
          isInternal={isInternal}
        />
        <LenderFeesAlert isBroker={isBroker} />
        {!isBroker && (
          <AddFeeButton
            pricerSummaryStore={pricerSummaryStore}
            loanAmount={loanAmount}
            onAddFee={handleAddFee}
            feeOptions={feeOptions.filter(option => !excludedLenderFeeOptions.includes(option.value))}
          />
        )}
        <Box>
          {fees.filter(row => row.category === 'Lender').map((fee, index) => (
            <Box className={classes.feeRow} key={index}>
              <Typography className={classes.feeLabel}>{fee.label}:</Typography>
              <Typography className={classes.feeValue}>
                {formatCurrency(fee.value)}

                {!isBroker && (
                  <IconButton
                    testId="check-button"
                    aria-label="delete"
                    size="small"
                  >
                    <Delete
                      fontSize="small"
                      onClick={event => {
                        handleDelete(fee.index);
                        excludedLenderFeeOptions.splice(excludedLenderFeeOptions.indexOf(fee.label), 1);
                      }}
                    />
                  </IconButton>
                )}
              </Typography>
            </Box>
          ))}
          <Box
            style={{ margin: '12px 0', borderBottom: '1px solid #e5e7eb' }}
          />
          <Box className={classes.feeRow}>
            <Typography className={classes.feeLabel}>
              Total allocated fees:
            </Typography>
            <Typography className={classes.feeValue}>
              {formatCurrency(customQuote.totalNonRocFees)}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }
);

const CapitalProviderFeesAlert = observer(({ isBroker }) => {
  const alertClasses = useStyles();
  return (
    <Alert severity="info" className={alertClasses.infoAlert}>
        {isBroker ?
          `Points and fees that go to the "Buydown" are needed in order to get the selected rate scenario.`
        :
          `Points and fees that go to the Capital Provider buy down the rate.
          To get the rate listed, you need to allocate the minimum points/fees
          to the Capital Provider.`
        }
    </Alert>
  );
});

const LenderFeesAlert = observer(({ isBroker }) => {
  const alertClasses = useStyles();
  return (
    <Alert severity="info" className={alertClasses.infoAlert}>
        {isBroker ?
          `Points and fees that are in the "Non-Buydown" don't impact the rate.`
        :
          `Points and fees that go to the Lender don't impact the rate.`
        }
    </Alert>
  );
});

const PricingTabAlert = observer(({ isPricingTab }) => {
  const alertClasses = useStyles();
  return isPricingTab ? (
    <Alert severity="info" className={alertClasses.infoAlert}>
      <AlertTitle>Info</AlertTitle>
      <ul>
        <li>
          Please note this pricing will only affect your buy down fees and
          points that are due to the capital provider. If you wish to change the
          points and fees due to you, please go to the lender fees task.
        </li>
      </ul>
    </Alert>
  ) : null;
});

const OtherFeesCard = observer(({ customQuote }) => {
  const classes = useStyles();

  return (
    customQuote?.fees?.addOnUwFee ? (
      <>
        <Box className={classes.card}>
          <Typography className={classes.cardTitle}>Other Capital Provider Fees</Typography>
          <Box>
            <Box className={classes.feeRow}>
              <Typography className={classes.feeLabel}>{'Underwriting Fee'}:</Typography>
              <Typography className={classes.feeValue}>
                {formatCurrency(customQuote.fees.addOnUwFee)}
              </Typography>
            </Box>
          </Box>
        </Box>
        <br/>
      </>
    ) : <></>
  );
});

export function getClosingFees(store: PricerSummaryStore) {
  return [
    {
      label: 'Legal Review Fee',
      value: store.closingLegalReviewFee,
    },
    {
      label: 'Servicing Set Up Fee',
      value: DEFAULT_FEE_RETAIL_SERVICING_SETUP,
    },
    { label: 'Corelogix Fee', value: DEFAULT_FEE_CORELOGIX },
  ];
}

const ClosingFeesCard = observer(({ store }: { store: PricerSummaryStore }) => {
  const classes = useStyles();
  const fees = getClosingFees(store);

  return (
    <Box className={classes.card}>
      <Typography className={classes.cardTitle}>Closing Fees</Typography>
      <Box>
        {fees.map((fee, index) => (
          <Box className={classes.feeRow}>
            <Typography className={classes.feeLabel}>{fee.label}:</Typography>
            <Typography className={classes.feeValue}>
              {formatCurrency(fee.value)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
});

const FeeAllocationWarning = observer(
  ({ totalAllocatedFees, pointsAndFees }) => {
    return (
      <Alert severity="warning">
        {totalAllocatedFees < pointsAndFees
          ? `You still need to allocate ${formatCurrency(
              pointsAndFees - totalAllocatedFees
            )} in fees to get the selected lowest rate.`
          : 'You have allocated more than the necessary fees.'}
      </Alert>
    );
  }
);

interface AddFeeButtonProps {
  pricerSummaryStore: PricerSummaryStore;
  loanAmount: number;
  onAddFee: (feeName: string, feeAmount: number, feePercent: number) => void;
  feeOptions: any;
}

const AddFeeButton = observer(({ pricerSummaryStore, loanAmount, onAddFee, feeOptions }: AddFeeButtonProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [feeName, setFeeName] = useState('');
  const [feeAmount, setFeeAmount] = useState(0);
  const [feePercent, setFeePercent] = useState(0);
  const classes = useStyles();

  const handleAddFee = () => {
    if(!feeName) {
      pricerSummaryStore.globalStore.notificationStore.showErrorNotification({
        message: 'Please choose the fee name.',
      });
      return;
    }
    setIsEditing(false);
    onAddFee(feeName, feeAmount, feePercent);
    setFeeName('');
    setFeeAmount(0);
    setFeePercent(0);
  };

  const handleChangeAmount = (_, newValue) => {
    setFeeAmount(newValue);
    setFeePercent(loanAmount > 0 ? (newValue / loanAmount) * 100 : 0);
  };

  const handleChangePercent = (_, newValue) => {
    setFeePercent(newValue);
    setFeeAmount(loanAmount > 0 ? (newValue / 100) * loanAmount : 0);
  };

  return isEditing ? (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <SelectField
          disabled={false}
          value={feeName}
          label="Fee Name"
          fullWidth
          options={feeOptions}
          onChange={value => {
            setFeeName(value);
          }}
          variant={'outlined'}
          testId={'fee-name'}
        />
      </Grid>
      <Grid item xs={12}>
        <Box className={classes.customFieldInputContainer}>
          <Box className="currency-field-container" style={{ width: '50%' }}>
            <CurrencyField
              variant={'outlined'}
              testId={'fee-amount'}
              fullWidth
              value={isNil(feeAmount) ? '' : feeAmount}
              onChange={handleChangeAmount}
            />
          </Box>
          <Box className="percentage-field-container" style={{ width: '50%' }}>
            <PercentageField
              variant={'outlined'}
              testId={'fee-percent'}
              fullWidth
              value={isNil(feePercent) ? '' : feePercent}
              onChange={handleChangePercent}
            />
          </Box>
        </Box>
      </Grid>
      <Grid item xs={6}>
        <Button
          testId="add-capital-provider-fee"
          variant="contained"
          fullWidth
          color="primary"
          size="small"
          onClick={handleAddFee}
        >
          Add
        </Button>
      </Grid>
      <Grid item xs={6}>
        <Button
          testId="add-capital-provider-fee"
          variant="contained"
          color="default"
          size="small"
          fullWidth
          onClick={() => setIsEditing(false)}
        >
          Cancel
        </Button>
      </Grid>
    </Grid>
  ) : (
    <Button
      testId="add-capital-provider-fee"
      variant="contained"
      color="primary"
      onClick={() => setIsEditing(true)}
    >
      + Add Fee
    </Button>
  );
});
